<template>
  <div class="buttons">
    <nav
      class="sr-only"
      :aria-label="$t('web.widgets.calculator.buttongroup.navigation')"
    >
      <ul class="buttons__group-list">
        <li v-for="group, groupName in buttonGroups" :key="groupName">
          <a :href="`#calculator-${groupName}`">
            {{ $t(`web.widgets.calculator.buttongroup.${groupName}`) }}
          </a>
        </li>
      </ul>
    </nav>
    <div
      class="buttons__grid"
      :class="numeric ? 'buttons__grid--numeric' : ''"
      :aria-label="$t('web.widgets.calculator.buttons.title')"
      role="group"
    >
      <template v-for="group, groupName in buttonGroups">
        <template v-for="(button, index) in group">
          <EButton
            v-if="button.symbol === 'angle-units'"
            :id="index === 0 ? `calculator-${groupName}` : null"
            :key="button.symbol"
            :aria-label="modeSwitchAriaLabel"
            v-bind="button.props"
            class="buttons__double"
            data-test-id="calculator-angle-units"
            @click="toggleAngleUnits"
          >
            <span
              class="buttons__angle"
              :class="radiansSelected ? 'buttons__angle--selected' : ''"
            >
              Rad
            </span>
            <span
              class="buttons__angle"
              :class="degreesSelected ? 'buttons__angle--selected' : ''"
            >
              Deg
            </span>
          </EButton>
          <EButton
            v-else
            :id="index === 0 ? `calculator-${groupName}` : null"
            :key="button.symbol"
            v-bind="button.props"
            v-on="button.listeners"
            @click="onButtonClick(button.symbol)"
          >
            {{ button.symbol }}
          </EButton>
        </template>
      </template>
    </div>
    <div class="buttons__tabs">
      <ESegmentedButton
        class="buttons__tabs-button"
        :segments="[
          { label: '123', onClick: toggleNumeric, selected: numeric },
          { label: 'Fx', onClick: toggleScientific, selected: !numeric },
        ]"
      />
    </div>
  </div>
</template>

<script>
import EButton from '~~/common/vue/components/button';
import screen from '~~/common/vue/mixins/screen';
import ESegmentedButton from '~~/common/vue/components/segmented-button';
import { trackClick, TYPE_RICH } from '~~/search/common/js/client/analytics/rich-content';

import { tabletUp, mobile, groups } from './buttons-config';
import { DEGREES, RADIANS } from './constants';

export default {
  name: 'ECalculatorButtons',
  components: {
    EButton,
    ESegmentedButton,
  },
  mixins: [screen],
  props: {
    angleUnits: {
      type: String,
      validator: (value) => [DEGREES, RADIANS].includes(value),
      required: true,
    },
    decimalSeparator: {
      type: String,
      required: true,
    },
    invertedFunctions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    numeric: true,
  }),
  computed: {
    radiansSelected() {
      return this.angleUnits === RADIANS;
    },
    degreesSelected() {
      return this.angleUnits === DEGREES;
    },
    // Buttons which have a HTML form type
    // or their config depends on the component state.
    specialButtons() {
      return {
        'AC': {
          symbol: 'AC',
          props: {
            'type': 'reset',
            'variant': 'outline',
            'data-test-id': 'calculator-AC',
            'aria-label': this.$t('web.widgets.calculator.allclear'),
          },
        },
        '=': {
          symbol: '=',
          props: {
            'type': 'submit',
            'variant': 'solid-green',
            'aria-label': this.$t('web.widgets.calculator.equals'),
            'data-test-id': 'calculator-=',
          },
        },
        'decimalseparator': {
          symbol: this.decimalSeparator,
          props: {
            'data-test-id': 'calculator-decimalseparator',
            'variant': 'outline',
          },
          listeners: { click: () => this.insert(this.decimalSeparator, true) },
        },
        'angle-units': {
          symbol: 'angle-units',
          props: {},
        },
      };
    },
    buttonGroups() {
      const buttonsConfig = this.isTabletUp ? tabletUp : mobile;
      const buttonGroups = {
        [groups.DIGITS]: [],
        [groups.OPERATORS]: [],
        [groups.CONTROLS]: [],
        [groups.FUNCTIONS]: [],
        [groups.CONSTANTS]: [],
      };

      buttonsConfig.forEach((config, index) => {
        const button = this.buttonFromConfig(config, index);
        buttonGroups[config.group].push(button);
      });

      return buttonGroups;
    },
    modeSwitchAriaLabel() {
      const key = this.radiansSelected ?
        'web.widgets.calculator.angleswitch.degrees' :
        'web.widgets.calculator.angleswitch.radians';
      return this.$t(key);
    },
  },
  methods: {
    // prepare a renderable button object based on the button configuration.
    buttonFromConfig(config, index) {
      if (this.specialButtons[config.symbol]) {
        const button = this.specialButtons[config.symbol];
        button.props.style = `order: ${index}`;
        return button;
      }
      // The displayed functions buttons differ depending on
      // whether the "Inverse" toggle is active.
      const symbol = this.getSymbol(config);
      const inserter = this.getInserter(config);

      const button = {
        symbol,
        props: {
          'data-test-id': `calculator-${symbol}`,
          'variant': config.button,
          'style': `order: ${index}`,
        },
        listeners: { click: () => inserter(symbol) },
      };

      const ariaLabelKey = this.invertedFunctions ?
        (config.inverseAriaLabel || config.ariaLabel) :
        config.ariaLabel;

      if (ariaLabelKey) {
        button.props['aria-label'] = this.$t(`web.widgets.calculator.${ariaLabelKey}`);
      }

      return button;
    },
    insert(character) {
      this.$emit('insert', character);
    },
    insertDigit(character) {
      this.$emit('insert-digit', character);
    },
    insertWithSpace(character) {
      this.$emit('insert-with-space', character);
    },
    insertWithBrackets(character) {
      this.$emit('insert-with-brackets', character);
    },
    invertFunctions() {
      this.$emit('invert-functions');
    },
    getSymbol({ symbol, inverseSymbol }) {
      return (this.invertedFunctions ? inverseSymbol : symbol) ?? symbol;
    },
    getInserter({ inserter, inverseInserter }) {
      return (this.invertedFunctions && inverseInserter) ?
        this[inverseInserter]?.bind(this) :
        this[inserter].bind(this);
    },
    toggleAngleUnits() {
      const newUnits = this.degreesSelected ? RADIANS : DEGREES;
      this.$emit('set-angle-units', newUnits);
      this.onButtonClick('angle_switch', newUnits);
    },
    toggleNumeric() {
      this.numeric = true;
      this.onButtonClick('numeric_keyboard');
    },
    toggleScientific() {
      this.numeric = false;
      this.onButtonClick('scientific_keyboard');
    },
    onButtonClick(symbol, property) {
      trackClick({
        name: 'calculator',
        type: TYPE_RICH,
        target: symbol,
        property,
      })();
    },
  },
};
</script>

<style lang="scss" scoped>
.buttons {
  overflow: hidden;
  opacity: $opacity-1l;
  margin-bottom: -$space-m;

  @include tablet {
    margin-bottom: 0;
    overflow: visible;
  }
}

.buttons__group-list {
  list-style-type: none;
}

.buttons__grid {
  display: grid;
  // For mobile, we split the grid into 24 slices.
  // The numeric (left) half consists of 4 columns, 3 slices wide.
  // The scientific (right) half consists of 3 columns, 4 slices wide.
  grid-template-columns: repeat(4, 3fr) repeat(3, 4fr);
  grid-template-rows: repeat(5, 1fr);
  gap: $space-1s;
  // We scale the grid so only half of it is visible.
  width: 200%;
  transition: transform $timing-s $easing;
  transform: translateX(-50%);

  @include tablet {
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(5, 1fr);
    transform: none;
    width: auto;
  }
}

.buttons__grid--numeric {
  transform: none;
}

.buttons__tabs {
  margin-top: $space-m;

  @include tablet {
    display: none;
  }
}

.buttons__tabs-button {
  width: 200px;
}

.buttons__double {
  grid-column: span 2;
}

.buttons__angle {
  display: inline-flex;

  & + & {
    &::before {
      content: '|';
      display: inline-flex;
      margin: 0 $space-s;
    }
  }
}

.buttons__angle--selected {
  color: var(--color-link-primary);
  text-decoration: underline;

  &::before {
    color: var(--color-button-content-secondary);
  }
}

.buttons__power-mobile {
  @include tablet {
    display: none;
  }
}

.buttons__last-function {
  display: none;

  @include tablet {
    display: unset;
  }
}

.buttons__equals-mobile {
  @include tablet {
    display: none;
  }
}
</style>
